// Admin → Ops → Waiting on OMS API (2026-10-29,
// docs/decisions/2026-10-29-return-label-pending-rma.md): every return step
// waiting on the OMS API (its RMA number, or the $0 RMA a reason code makes
// it create), its contact, the order, what it waits on, since when and the
// last check, plus the ones finished, timed out or failed in the last two
// days. Rows from server/src/routes/return-labels.js (no customer data).

import { Fragment, useEffect, useState } from 'react'
import { Alert, Box, Button, Chip, CircularProgress, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { fetchWaitingOnOms } from './api.js'
import { formatEtTime } from '@cxhub/shared'
import { convNumberPath } from './contactPath.js'
import { PendingStepChecklist } from './ReturnLabelPending.jsx'
import { finishedChipText, waitingOnText } from './pendingLabels.js'

export default function WaitingOnOmsView() {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [open, setOpen] = useState(null)
  const load = () => {
    setError(null)
    fetchWaitingOnOms().then(setData).catch((e) => setError(e?.message ?? 'Could not load the list'))
  }
  useEffect(load, [])
  const rows = data?.rows ?? []
  return (
    <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }} data-testid="waiting-on-oms-view">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6">Waiting on OMS API</Typography>
        {data?.counts && <Chip size="small" color={data.counts.waiting ? 'warning' : 'default'} label={`${data.counts.waiting} waiting`} />}
        {data?.counts?.timedOut24h > 0 && <Chip size="small" color="error" label={`${data.counts.timedOut24h} timed out (24 h)`} />}
        <Button size="small" onClick={load}>Refresh</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Return steps whose label waits on the OMS API: the RMA number (checked every 2 minutes for 24 hours) or the $0 RMA a
        reason code makes it create (every 5 minutes for 48 hours). CXHub finishes each one when it can; one that runs out of
        time is a Failures row, an Ops alert, and its contact back in the queue.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {!data && !error && <CircularProgress size={20} />}
      {data && rows.length === 0 && <Typography variant="body2">Nothing waiting.</Typography>}
      {rows.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Contact</TableCell>
              <TableCell>Order</TableCell>
              <TableCell>Waiting on</TableCell>
              <TableCell>RMA</TableCell>
              <TableCell>Since (ET)</TableCell>
              <TableCell>Last check (ET)</TableCell>
              <TableCell>State</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <Fragment key={r.id}>
                <TableRow data-testid="waiting-on-oms-row">
                  <TableCell>{r.convNumber != null ? <Link href={convNumberPath(r.convNumber)} target="_blank" rel="noopener">{r.convNumber}</Link> : '-'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{r.erpOrderId ?? '-'}</TableCell>
                  <TableCell>{waitingOnText(r)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{r.erpRmaId ?? r.trqRmaId ?? '-'}</TableCell>
                  <TableCell>{formatEtTime(r.createdAt) ?? '-'}</TableCell>
                  <TableCell>{formatEtTime(r.lastCheckedAt) ?? '-'}</TableCell>
                  <TableCell>{r.status === 'waiting' ? <Chip size="small" color="warning" label={r.chip} /> : <Chip size="small" color={r.status === 'done' ? 'success' : 'error'} label={finishedChipText(r)} />}</TableCell>
                  <TableCell><Button size="small" onClick={() => setOpen(open === r.id ? null : r.id)}>{open === r.id ? 'Hide' : 'Checklist'}</Button></TableCell>
                </TableRow>
                {open === r.id && (
                  <TableRow>
                    <TableCell colSpan={8}><PendingStepChecklist row={r} compact /></TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  )
}
